import { Tooltip } from '@mui/material'
import { C1Icon } from 'components/core/icons/C1Icon'
import { differenceInDays } from 'date-fns'
import { CircleAlert, CircleCheck, MemoryStick, TriangleAlert } from 'lucide-react'
import { AccessReviewConnectorSource } from 'pbts/c1/api/accessreview/v1/accessreview'
import { ConnectorSyncLifecycle, ConnectorSyncLifecycleStatus, FileConfig } from 'pbts/c1/api/app/v1/connector'
import { Any } from 'pbts/google/protobuf/any'
import { Timestamp as pbtsTimestamp } from 'pbts/google/protobuf/timestamp'
import { FormattedMessage } from 'react-intl'

// Constants for outdated thresholds in days
const FILE_OUTDATED_THRESHOLD = 7
const ENV_OUTDATED_THRESHOLD = 2

interface ConnectorStatusAccuracyChipProps {
    configType?: string
    connectorSyncLifecycle?: ConnectorSyncLifecycle
    accessReviewConnectorSource?: AccessReviewConnectorSource
}

// Helper function to determine if connector is outdated based on type
export const isOutdated = (diffDays: number, isFileConnector: boolean): boolean =>
    (isFileConnector ? diffDays >= FILE_OUTDATED_THRESHOLD : diffDays >= ENV_OUTDATED_THRESHOLD)

export const ConnectorStatusAccuracyStatus: React.FC<ConnectorStatusAccuracyChipProps> = ({
    configType,
    connectorSyncLifecycle,
    accessReviewConnectorSource,
}) => {
    if (!connectorSyncLifecycle || !accessReviewConnectorSource) {
        return null
    }
    // adding extra check below because older connectors show status as ERROR if they are not connected
    if (connectorSyncLifecycle?.status === ConnectorSyncLifecycleStatus.SYNC_LIFECYCLE_STATUS_NOT_CONNECTED
        || (!accessReviewConnectorSource?.syncEndAt
            && connectorSyncLifecycle?.status === ConnectorSyncLifecycleStatus.SYNC_LIFECYCLE_STATUS_ERRORED)
    ) {
        return (
            <Tooltip
                title={
                    <FormattedMessage
                        id="component_accuracy_not_connected_tooltip"
                        defaultMessage="Not connected"
                    />
                }
                placement="top"
            >
                <C1Icon icon={CircleAlert} sx={{ color: 'warning.main' }} />
            </Tooltip>
        )
    }
    if (connectorSyncLifecycle?.status === ConnectorSyncLifecycleStatus.SYNC_LIFECYCLE_STATUS_ERRORED
        || connectorSyncLifecycle?.status === ConnectorSyncLifecycleStatus.SYNC_LIFECYCLE_STATUS_ERRORED_NO_DATA) {
        return (
            <Tooltip
                title={
                    <FormattedMessage
                        id="component_accuracy_error_tooltip"
                        defaultMessage="Connector error"
                    />
                }
                placement="top"
            >
                <C1Icon icon={CircleAlert} sx={{ color: 'error.main' }} />
            </Tooltip>
        )
    }
    if (!accessReviewConnectorSource?.syncEndAt || !configType) {
        return (
            <Tooltip
                title={
                    <FormattedMessage
                        id="component_accuracy_not_connected_tooltip"
                        defaultMessage="Not configured"
                    />
                }
                placement="top"
            >
                <C1Icon icon={CircleAlert} sx={{ color: 'warning.main' }} />
            </Tooltip>
        )
    }

    const isFileConnector = Any.typeUrlToName(configType) === FileConfig.typeName
    const syncEndDate = pbtsTimestamp.toDate(accessReviewConnectorSource.syncEndAt)
    const daysSinceLastSync = differenceInDays(new Date(), syncEndDate)
    const isConnectorOutdated = isOutdated(daysSinceLastSync, isFileConnector)

    return isConnectorOutdated ? (
        <Tooltip
            title={
                <FormattedMessage
                    id="component_accuracy_outdated_tooltip"
                    defaultMessage="{isFileConnector, select, true {File source not updated for more than 7 days} other {Connector hasn't synced in more than 2 days}}"
                    values={{ isFileConnector }}
                />
            }
            placement="top"
        >
            {isFileConnector ? <C1Icon icon={MemoryStick} sx={{ color: 'warning.main' }} /> : <C1Icon icon={TriangleAlert} sx={{ color: 'warning.main' }} />}
        </Tooltip>
    ) : (
        <C1Icon icon={CircleCheck} sx={{ color: 'success.main' }} />
    )
}
